import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import {
  CandlestickChart,
  Coins,
  LayoutDashboard,
  LogOut,
  PiggyBank,
  PieChart,
  ScanSearch,
  Settings,
  Target,
  TrendingUp,
  User,
  type LucideIcon,
} from 'lucide-react';
import { authService, userService, type CurrentUserInfo } from '@/services';
import { useI18n } from '@/i18n/use-i18n';
import { cn } from '@/utils/cn';
import './bottom-nav.css';

type NavItem = {
  path: string;
  label: string;
  icon: LucideIcon;
};

export function BottomNav() {
  const { strings } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems: NavItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/contributions', label: 'Aportes', icon: PiggyBank },
    { path: '/asset-classes', label: 'Carteira', icon: PieChart },
    { path: '/target-portfolio', label: 'Ativos', icon: Target },
    { path: '/market', label: 'Mercado', icon: CandlestickChart },
    { path: '/proventos', label: 'Proventos', icon: Coins },
    { path: '/fii-radar', label: 'FII Radar', icon: ScanSearch },
    { path: '/stock-radar', label: 'Ações Radar', icon: TrendingUp },
  ];

  const { data: currentUser } = useQuery<CurrentUserInfo>({
    queryKey: ['current-user'],
    queryFn: userService.getCurrentUser,
    staleTime: 1000 * 60 * 5,
  });

  const handleSignOut = async () => {
    await authService.signOut();
    navigate('/login', { replace: true });
  };

  return (
    <nav className="bottom-nav" aria-label={strings.sidebar.navAriaLabel}>
      {navItems.map((item) => {
        const Icon = item.icon;
        const isActive =
          item.path === '/market'
            ? location.pathname.startsWith('/market')
            : location.pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            className={cn('bottom-nav__item', isActive && 'bottom-nav__item--active')}
            aria-current={isActive ? 'page' : undefined}
          >
            <span className="bottom-nav__icon" aria-hidden="true">
              <Icon size={20} strokeWidth={2} />
            </span>
            <span className="bottom-nav__label">{item.label}</span>
          </Link>
        );
      })}

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="bottom-nav__item"
            aria-label={strings.header.profileLabel}
          >
            <span className="bottom-nav__icon" aria-hidden="true">
              <User size={20} strokeWidth={2} />
            </span>
            <span className="bottom-nav__label">
              {typeof currentUser?.name === 'string' ? currentUser.name.split(' ')[0] : strings.header.accountFallback}
            </span>
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="bottom-nav__user-menu"
            align="end"
            side="top"
            sideOffset={8}
          >
            <DropdownMenu.Item
              className="bottom-nav__user-menu-item"
              onSelect={() => navigate('/settings')}
            >
              <Settings size={16} />
              <span>{strings.header.settingsLabel}</span>
            </DropdownMenu.Item>
            <DropdownMenu.Separator className="bottom-nav__user-menu-separator" />
            <DropdownMenu.Item
              className="bottom-nav__user-menu-item bottom-nav__user-menu-item--danger"
              onSelect={handleSignOut}
            >
              <LogOut size={16} />
              <span>{strings.header.signOutLabel}</span>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </nav>
  );
}
